import { inject } from '@angular/core';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { UnifiedCacheService } from '@/src/app/shared/services';

// ─── SAYFALAMA TİPLERİ ───────────────────────────────────────────────────────

export interface ISayfaliSonuc<T> {
  toplam: number;
  liste: T[];
}

export interface ISayfalama {
  sayfa: number;
  limit: number;
}

// ─── ABSTRACT DB BASE SERVİS ─────────────────────────────────────────────────

export abstract class DbBaseService {
  protected FikoYT = inject(FikoYTService);
  protected req = inject(FikoYTRequestService);
  private cache = inject(UnifiedCacheService);

  protected cacheTTL = 60_000;

  protected cached<T>(anahtar: string, ttl?: number): T | null {
    return this.cache.al<T>(anahtar, ttl || this.cacheTTL);
  }

  protected cacheSet<T>(anahtar: string, veri: T): T {
    return this.cache.set<T>(anahtar, veri, this.cacheTTL);
  }

  protected cacheTemizle(onekFiltre?: string): void {
    this.cache.temizle(onekFiltre || undefined);
  }

  // ─── Temel DB İşlemleri ─────────────────────────────────────────────────────

  protected aggregate<T = any>(komut: string, pipeline: any[]): Promise<T[]> {
    return this.req.aggregate<T>(komut, pipeline);
  }

  protected ekle<T = any>(komut: string, veri: any): Promise<T> {
    return this.req.ekle<T>(komut, veri);
  }

  protected guncelle(komut: string, filter: any, veri: any): Promise<any> {
    return this.req.guncelle(komut, filter, veri);
  }

  protected sil(komut: string, filter: any): Promise<any> {
    return this.req.sil(komut, filter);
  }

  // ─── Sayfalı Sorgu Helper ──────────────────────────────────────────────────

  protected sayfaliPipeline(
    matchStage: any,
    sortStage: any,
    sayfa: number,
    limit: number,
    ekPipeline: any[] = [],
  ): any[] {
    return [
      { $match: matchStage },
      ...ekPipeline,
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          liste: [
            { $sort: sortStage },
            { $skip: (sayfa - 1) * limit },
            { $limit: limit },
          ],
        },
      },
    ];
  }

  protected sayfaliSonucCoz<T>(sonuc: any[]): ISayfaliSonuc<T> {
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      liste: (facet?.liste || []) as T[],
    };
  }

  // ─── Firma İzolasyonu ───────────────────────────────────────────────────────

  protected firmaMatch(firmaId: string, ekFiltre?: any): any {
    return { firmaId, ...(ekFiltre || {}) };
  }

  // ─── Denetim Kaydı ─────────────────────────────────────────────────────────

  protected async denetimKaydet(
    firmaId: string,
    islem: string,
    modul: string,
    detay: Record<string, any> = {},
  ): Promise<void> {
    const kullanici = this.FikoYT.kimlik.aktifKullaniciJson();
    await this.ekle('FikoYT.sistem.DB.hareketler.ekle', {
      vt: 'FikoYT.sistem.denetim',
      firmaId,
      islem,
      modul,
      detay,
      kullaniciId: kullanici?._id || 'bilinmiyor',
      tarih: new Date().toISOString(),
    }).catch(() => {});
  }

  // ─── Aktif Kullanıcı ───────────────────────────────────────────────────────

  protected get aktifKullaniciId(): string {
    return this.FikoYT.kimlik.aktifKullaniciJson()?._id || '';
  }

  protected get aktifKullanici(): any {
    return this.FikoYT.kimlik.aktifKullaniciJson();
  }

  // ─── Zaman Yardımcıları ─────────────────────────────────────────────────────

  protected bugunBaslangic(): string {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }

  protected simdi(): string {
    return new Date().toISOString();
  }
}
